import React, { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '../components/Navbar';
import City3DMap, { PLACES, STREET_SEGMENTS, INTERSECTIONS } from '../components/simulator/City3DMap';
import GameSetupModal, { getRandomAttacker } from '../components/simulator/GameSetupModal';
import WelcomeGameModal from '../components/simulator/WelcomeGameModal';
import MasteryCongratsModal from '../components/simulator/MasteryCongratsModal';
import FirstSuccessPopup from '../components/simulator/FirstSuccessPopup';
import sfx from '../utils/soundEffects';
import {
  loadUserXP, saveUserXP,
  loadUserPV, saveUserPV,
  loadUnlockedFeatures, saveUnlockedFeatures,
  loadTrustedContactsCount, saveTrustedContactsCount, CONTACT_XP_COST, MAX_TRUSTED_CONTACTS,
  loadCitizenCount, saveCitizenCount,
  loadAppAdoptionRate, saveAppAdoptionRate,
  loadTimeMultiplier, saveTimeMultiplier,
  calculateRewards, checkUnlockPrerequisite,
  MAX_AGILITY_CAP, MAX_CHARISMA_CAP,
  MAX_CITIZEN_COUNT, MAX_APP_ADOPTION_RATE, ALL_UNLOCKABLE_FEATURES,
  loadMasteryPopupShown, saveMasteryPopupShown,
  loadFirstSuccessPopupShown, saveFirstSuccessPopupShown
} from '../utils/xpProgression';

const INITIAL_PARENT = {
  name: 'Sarah',
  role: 'user',
};

const INITIAL_PARENT_TILES = {
  audioLive: false,
  cameraCloud: false,
  accidentDetection: true,
  shortcutHardware: false,
  smartwatch: false,
  guardians: false,
  phoneInHand: false,
};

let lastPhoneLoc = null;

// Compteur de tours consécutifs sans apparition, par emplacement
let locMissStreak = { in_bag: 0, in_pocket: 0, in_hand: 0 };
// Seuil de forçage par emplacement : "main" forcée après 2 tours d'absence, sac/poche après 3
const LOC_FORCE_THRESHOLD = { in_bag: 3, in_pocket: 3, in_hand: 2 };

// Tirage aléatoire équilibré de l'emplacement du smartphone : 33.3% Sac, 33.3% Poche, 33.3% Main
// (alternance garantie + un emplacement absent trop longtemps est forcé, "main" en priorité après 2 tours)
export const getRandomPhoneLocation = () => {
  const locs = ['in_bag', 'in_pocket', 'in_hand'];
  // "in_hand" vérifiée en priorité : son seuil de forçage (2 tours) est plus court que sac/poche (3 tours)
  const forced = ['in_hand', 'in_bag', 'in_pocket'].find(loc => locMissStreak[loc] >= LOC_FORCE_THRESHOLD[loc]);

  let picked;
  if (forced) {
    picked = forced;
  } else {
    const choices = locs.filter(l => l !== lastPhoneLoc);
    picked = choices[Math.floor(Math.random() * choices.length)];
  }

  locs.forEach(loc => {
    locMissStreak[loc] = (loc === picked) ? 0 : locMissStreak[loc] + 1;
  });

  lastPhoneLoc = picked;
  return picked;
};

// Compteur de fois consécutives où le téléphone était en main SANS être arraché (forçage après 2 fois)
let inHandNoSnatchStreak = 0;

// Détermine si l'agresseur arrache le téléphone des mains (25% de base, forcé après 2 fois sans vol)
export const shouldSnatchPhone = (location) => {
  if (location !== 'in_hand') return false;

  if (inHandNoSnatchStreak >= 2) {
    inHandNoSnatchStreak = 0;
    return true;
  }

  const snatched = Math.random() < 0.25;
  inHandNoSnatchStreak = snatched ? 0 : inHandNoSnatchStreak + 1;
  return snatched;
};

const VERT_STREETS = [100, 260, 420, 580, 740, 900, 1010];
const HORIZ_STREETS = [80, 170, 260, 350, 440, 530];

const SimulatorPage = () => {
  const [parentProfile] = useState(INITIAL_PARENT);
  const [childrenProfiles, setChildrenProfiles] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [parentTiles, setParentTiles] = useState(INITIAL_PARENT_TILES);
  const [activePlaceId, setActivePlaceId] = useState('school');

  // Gamification & Attributs RPG (30 PV Agilité, 25 PV Charisme)
  const [userRPG, setUserRPG] = useState({
    name: 'Sarah',
    phoneMode: 'in_bag',
    phoneInHandLevel: 25,
    agility: 30,
    composure: 25,
  });
  const [currentAttackerRPG, setCurrentAttackerRPG] = useState(getRandomAttacker);

  // État du Système Tour par Tour (Pauses Automatiques & Choix)  // Système RPG & Tour par tour
  const [isDuelModalOpen, setIsDuelModalOpen] = useState(false);
  const [duelPhase, setDuelPhase] = useState('victim_action'); // 'victim_action' | 'protector_strike' | 'victim_failed'
  const [activeProtector, setActiveProtector] = useState(null);
  const [currentAgility, setCurrentAgility] = useState(30);
  const [currentCharisma, setCurrentCharisma] = useState(25);
  const [actionHistory, setActionHistory] = useState([]);
  const [currentPhoneLocation, setCurrentPhoneLocation] = useState(userRPG.phoneMode || 'in_bag');
  const [phoneLocationAtAlert, setPhoneLocationAtAlert] = useState(userRPG.phoneMode || 'in_bag');
  const hasTriggeredContactDuelRef = useRef(false);
  const wasAlertTriggeredRef = useRef(false);
  // Empêche le double comptage des récompenses (StrictMode ré-invoque les updaters, ou double appel manuel + automatique)
  const incidentFinalizedRef = useRef(false);
  // Dernier chiffre de la seconde du décompte d'agression pour jouer un "tac" à chaque seconde écoulée
  const lastAggressionTickRef = useRef(null);

  // Journal et Télémétrie
  const [logs, setLogs] = useState([
    { time: '17:40:00', text: 'Simulateur Tour par Tour MonAlerte prêt. Cliquez sur "Démarrer une Partie".', color: '#93c5fd', type: 'info' },
    { time: '17:40:02', text: 'Le jeu se mettra en pause automatique lors de l’agression et des interventions des Protecteurs !', color: '#ffd873', type: 'info' },
  ]);

  const addLog = useCallback((text, type = 'info', color = '#e2e8f0') => {
    const now = new Date();
    const timeStr = now.toTimeString().split(' ')[0];
    setLogs(prev => [
      { time: timeStr, text, type, color },
      ...prev.slice(0, 45),
    ]);
  }, []);

  // Progression Expérience (XP), Points de Vie (PV) et Déblocages
  const [userXP, setUserXP] = useState(loadUserXP);
  const [userPV, setUserPV] = useState(loadUserPV);
  const [unlockedFeatures, setUnlockedFeatures] = useState(loadUnlockedFeatures);
  const [trustedContactsCount, setTrustedContactsCount] = useState(loadTrustedContactsCount);
  const userXPRef = useRef(userXP);
  const userPVRef = useRef(userPV);
  const unlockedFeaturesRef = useRef(unlockedFeatures);
  const trustedContactsCountRef = useRef(trustedContactsCount);
  useEffect(() => {
    userXPRef.current = userXP;
  }, [userXP]);
  useEffect(() => {
    userPVRef.current = userPV;
  }, [userPV]);
  useEffect(() => {
    unlockedFeaturesRef.current = unlockedFeatures;
  }, [unlockedFeatures]);
  useEffect(() => {
    trustedContactsCountRef.current = trustedContactsCount;
  }, [trustedContactsCount]);

  // Popup unique de félicitations "Maîtrise complète" (tout débloqué + ville au maximum)
  const [showMasteryPopup, setShowMasteryPopup] = useState(false);
  const masteryPopupShownRef = useRef(loadMasteryPopupShown());

  // Popup unique de félicitations "Première réussite complète" (agression interrompue + suspect identifié),
  // affichée juste avant le tout premier rapport concerné (le rapport reste en attente le temps de la popup)
  const [showFirstSuccessPopup, setShowFirstSuccessPopup] = useState(false);
  const firstSuccessPopupShownRef = useRef(loadFirstSuccessPopupShown());
  const pendingIncidentReportRef = useRef(null);

  const handleAddTrustedContact = useCallback(() => {
    if (trustedContactsCountRef.current >= MAX_TRUSTED_CONTACTS) {
      addLog(`⚠️ Limite maximale atteinte : 25 Contacts de Confiance au maximum !`, 'alert', '#f59e0b');
      return false;
    }
    if (userXPRef.current >= CONTACT_XP_COST) {
      const nextXP = userXPRef.current - CONTACT_XP_COST;
      setUserXP(nextXP);
      saveUserXP(nextXP);
      setTrustedContactsCount(prev => {
        const nextCount = Math.min(MAX_TRUSTED_CONTACTS, prev + 1);
        saveTrustedContactsCount(nextCount);
        return nextCount;
      });
      addLog(`📲 Contact ajouté : +1 Contact de Confiance (-${CONTACT_XP_COST} XP) ! Total : ${Math.min(MAX_TRUSTED_CONTACTS, (trustedContactsCountRef.current || 0) + 1)}/25`, 'success', '#ffd873');
      return true;
    }
    return false;
  }, [addLog]);

  const handleBuyPopulation = useCallback(() => {
    if (userPVRef.current >= 10) {
      sfx.playShield();
      const nextPV = userPVRef.current - 10;
      setUserPV(nextPV);
      saveUserPV(nextPV);
      setSimulationConfig(prev => {
        const nextCount = Math.min(200, prev.citizenCount + 10);
        saveCitizenCount(nextCount);
        return {
          ...prev,
          citizenCount: nextCount,
        };
      });
      addLog(`👥 Population augmentée : +10 Citoyens (-10 PV) !`, 'success', '#38bdf8');
      return true;
    }
    return false;
  }, [addLog]);

  const handleBuyProtectors = useCallback(() => {
    if (userPVRef.current >= 10) {
      sfx.playShield();
      const nextPV = userPVRef.current - 10;
      setUserPV(nextPV);
      saveUserPV(nextPV);
      setSimulationConfig(prev => {
        const nextRate = Math.min(90, prev.appAdoptionRate + 10);
        saveAppAdoptionRate(nextRate);
        return {
          ...prev,
          appAdoptionRate: nextRate,
        };
      });
      addLog(`🛡️ Protecteurs augmentés : +10% Protecteurs (-10 PV) !`, 'success', '#f2b807');
      return true;
    }
    return false;
  }, [addLog]);

  const handleUnlockFeature = useCallback((featureId, costXP = 0, costPV = 0) => {
    // Vérification stricte des prérequis (arbre de déblocage)
    const currentUnlocked = unlockedFeaturesRef.current || [];
    const prereq = checkUnlockPrerequisite(featureId, currentUnlocked);
    if (!prereq.met) {
      addLog(`🔒 DÉBLOCAGE BLOQUÉ : ${prereq.reason}`, 'alert', '#ef4444');
      return false;
    }

    if (userXPRef.current < costXP || userPVRef.current < costPV) return false;

    sfx.playSuccess();
    const nextXP = userXPRef.current - costXP;
    const nextPV = userPVRef.current - costPV;

    setUserXP(nextXP);
    setUserPV(nextPV);
    saveUserXP(nextXP);
    saveUserPV(nextPV);

    setUnlockedFeatures(prev => {
      if (prev.includes(featureId)) return prev;
      const next = [...prev, featureId];
      saveUnlockedFeatures(next);
      return next;
    });

    let costMsg = '';
    if (costPV > 0) costMsg += `-${costPV} PV `;
    if (costPV > 0 && costXP > 0) costMsg += '• ';
    if (costXP > 0) costMsg += `-${costXP} XP`;

    addLog(`🔓 DÉBLOCAGE RPG : "${featureId}" débloqué avec succès (${costMsg}) !`, 'success', '#ffd873');
    return true;
  }, [addLog]);

  const handleSpendPV = useCallback((statType, amount = 1) => {
    if (userPVRef.current < amount) return false;
    const nextPV = userPVRef.current - amount;
    setUserPV(nextPV);
    saveUserPV(nextPV);

    if (statType === 'agility') {
      setCurrentAgility(prev => Math.min(MAX_AGILITY_CAP, prev + amount));
      setUserRPG(prev => {
        const next = Math.min(MAX_AGILITY_CAP, (prev.agility || 30) + amount);
        return { ...prev, agility: next };
      });
      addLog(`⚡ +${amount} Vitesse & Réflexes attribué (-${amount} PV) !`, 'success', '#38bdf8');
    } else if (statType === 'composure') {
      setCurrentCharisma(prev => Math.min(MAX_CHARISMA_CAP, prev + amount));
      setUserRPG(prev => {
        const next = Math.min(MAX_CHARISMA_CAP, (prev.composure || 25) + amount);
        return { ...prev, composure: next };
      });
      addLog(`🛡️ +${amount} Charme & Sang-froid attribué (-${amount} PV) !`, 'success', '#4ade80');
    }
    return true;
  }, [addLog]);

  // Gestion de la Partie et du Choix de Rôle
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(true);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('user');
  const autoCrisisCountdownRef = useRef(0);

  const [simulationConfig, setSimulationConfig] = useState(() => ({
    citizenCount: loadCitizenCount(),
    appAdoptionRate: loadAppAdoptionRate(),
    deadZoneActive: false,
  }));
  const simulationConfigRef = useRef(simulationConfig);
  useEffect(() => {
    simulationConfigRef.current = simulationConfig;
  }, [simulationConfig]);

  // Popup affichée seulement à la fermeture du rapport d'enquête (pas immédiatement à la résolution de l'incident)
  const pendingMasteryPopupRef = useRef(false);

  // Vérifie si le joueur a tout débloqué + ville au maximum, et si l'agression est interrompue
  // ET l'agresseur identifié dans le même incident : arme la popup, affichée à la fermeture du rapport.
  const checkMasteryAchievement = useCallback((isAttackInterrupted, isSuspectArrested) => {
    if (masteryPopupShownRef.current) return;
    if (!isAttackInterrupted || !isSuspectArrested) return;

    const allUnlocked = ALL_UNLOCKABLE_FEATURES.every(key => (unlockedFeaturesRef.current || []).includes(key));
    const maxCitizens = (simulationConfigRef.current?.citizenCount || 0) >= MAX_CITIZEN_COUNT;
    const maxProtectors = (simulationConfigRef.current?.appAdoptionRate || 0) >= MAX_APP_ADOPTION_RATE;

    if (allUnlocked && maxCitizens && maxProtectors) {
      masteryPopupShownRef.current = true;
      saveMasteryPopupShown(true);
      pendingMasteryPopupRef.current = true;
    }
  }, []);

  // Diffère l'affichage du rapport d'enquête : si c'est la toute première fois que l'agression est
  // interrompue ET l'agresseur identifié, on affiche d'abord la popup de félicitations, puis le rapport.
  const finalizeIncidentReport = useCallback((reportData, isAttackInterrupted, isSuspectArrested) => {
    const isFirstSuccess = !firstSuccessPopupShownRef.current && isAttackInterrupted && isSuspectArrested;

    if (isFirstSuccess) {
      firstSuccessPopupShownRef.current = true;
      saveFirstSuccessPopupShown(true);
      pendingIncidentReportRef.current = reportData;
      setShowFirstSuccessPopup(true);
    } else {
      setIncidentReport(reportData);
    }
  }, []);

  const handleCloseFirstSuccessPopup = useCallback(() => {
    setShowFirstSuccessPopup(false);
    setIncidentReport(pendingIncidentReportRef.current);
    pendingIncidentReportRef.current = null;
  }, []);

  const [timeMultiplier, setTimeMultiplierState] = useState(() => loadTimeMultiplier());
  const setTimeMultiplier = useCallback((speed) => {
    setTimeMultiplierState(prev => {
      const val = typeof speed === 'function' ? speed(prev) : speed;
      saveTimeMultiplier(val);
      return val;
    });
  }, []);
  const [isPaused, setIsPaused] = useState(false);
  const [incidentReport, setIncidentReport] = useState(null);

  // État de la simulation
  const [simulationState, setSimulationState] = useState({
    isNight: false,
    victim: {
      x: 260,
      y: 170,
      orientation: 'H',
      vx: 1.0,
      vy: 0,
      profileName: 'Sarah (Vous)',
      isInPersonalWhiteZone: false,
      isConfronting: false,
      confrontationTimer: 0,
      maxConfrontationDuration: 700,
      requiredGuardiansToFlee: 2,
      pauseTimer: 0,
    },
    childEntity: null,
    citizens: [],
    attacker: null,
    policeCar: null,
    activeAlert: false,
    alertStartTime: null,
    alertSimulatedSeconds: 0,
    duressPinTriggered: false,
    meshConnections: [],
  });

  const [stats, setStats] = useState({
    responseTime: '0s',
    guardiansAlerted: 0,
    vaultFiles: 0,
    meshHops: 0,
  });

  const handleStartGame = (config) => {
    setIsGameStarted(true);
    setIsSetupModalOpen(false);
    setIncidentReport(null);
    setIsPaused(false);
    setIsDuelModalOpen(false);
    setActiveProtector(null);
    setActionHistory([]);
    hasTriggeredContactDuelRef.current = false;
    wasAlertTriggeredRef.current = false;
    incidentFinalizedRef.current = false;
    lastAggressionTickRef.current = null;
    initCitizens();

    const role = config?.role || selectedRole;
    const childName = config?.childName || 'Lucas (10 ans)';

    // Choix aléatoire d'un profil d'agresseur (25% contre App SOS, 75% sans contre)
    const pickedAttacker = config?.attackerRPG || getRandomAttacker();
    setCurrentAttackerRPG(pickedAttacker);

    const uRPG = config?.userRPG || userRPG;
    if (config?.userRPG) {
      setUserRPG(config.userRPG);
      setCurrentAgility(config.userRPG.agility || 30);
      setCurrentCharisma(config.userRPG.composure || 25);
    } else {
      setCurrentAgility(uRPG.agility || 30);
      setCurrentCharisma(uRPG.composure || 25);
    }

    // Emplacement initial aléatoire (25% sac, 25% poche, 50% main)
    const initialLoc = getRandomPhoneLocation();
    setCurrentPhoneLocation(initialLoc);
    setPhoneLocationAtAlert(initialLoc);

    // Armement du compte à rebours d'agression UNIQUEMENT si explicitement demandé
    autoCrisisCountdownRef.current = config?.spawnAggressor ? 5000 : 0;

    if (role === 'parent') {
      const newChild = { id: 'child-1', name: childName, theme: 'Océan Bleuté' };
      setChildrenProfiles([newChild]);
      setActiveChildId('child-1');

      setSimulationState(prev => ({
        ...prev,
        activeAlert: false,
        attacker: null,
        alertStartTime: null,
        alertSimulatedSeconds: 0,
        victim: { ...prev.victim, isConfronting: false, confrontationTimer: 0 },
        childEntity: {
          x: 260,
          y: 170,
          orientation: 'V',
          vx: 0,
          vy: 1.0,
          name: childName,
          isInPersonalWhiteZone: false,
          pauseTimer: 0,
        },
      }));

      const activeTilesCount = Object.values(parentTiles).filter(Boolean).length;
      addLog(`🎮 Partie démarrée : Mode Parent / Famille activé pour ${childName} !`, 'success', '#4ade80');
      addLog(`🛡️ Protections armées (${activeTilesCount} tuiles). Déambulation urbaine... Agression imminente dans 5s !`, 'info', '#ffd873');
    } else if (role === 'protector') {
      setChildrenProfiles([]);
      setActiveChildId(null);
      setSimulationState(prev => ({
        ...prev,
        activeAlert: false,
        attacker: null,
        alertStartTime: null,
        alertSimulatedSeconds: 0,
        victim: { ...prev.victim, isConfronting: false, confrontationTimer: 0, profileName: 'Sarah (Protectrice)' },
        childEntity: null,
      }));
      addLog(`🎮 Partie démarrée : Mode Protecteur (Ange Gardien). Vous patrouillez dans la ville, prêt à secourir toute victime en détresse !`, 'success', '#10b981');
    } else {
      setChildrenProfiles([]);
      setActiveChildId(null);
      setSimulationState(prev => ({
        ...prev,
        activeAlert: false,
        attacker: null,
        alertStartTime: null,
        alertSimulatedSeconds: 0,
        victim: { ...prev.victim, isConfronting: false, confrontationTimer: 0, profileName: 'Sarah (Vous)' },
        childEntity: null,
      }));
      addLog(`🎮 Partie démarrée : Sarah en déambulation (Agilité: ${uRPG.agility}/100 • Charisme/Sang-froid: ${uRPG.composure || 90}/100).`, 'success', '#38bdf8');
      addLog(`🕵️ Profil de l'agresseur secret... Agression imminente dans 5s !`, 'info', '#ffd873');
    }
  };

  // Initialisation des citoyens sur la grande carte avec Charisme Protecteur
  const initCitizens = useCallback(() => {
    const count = simulationConfig.citizenCount;
    const adoption = simulationConfig.appAdoptionRate / 100;
    const newCitizens = [];

    for (let i = 0; i < count; i++) {
      const hasApp = Math.random() < adoption;
      const street = STREET_SEGMENTS[Math.floor(Math.random() * STREET_SEGMENTS.length)];
      let x, y, orientation, vx, vy;

      if (street.orientation === 'H') {
        const minX = street.xMin + 50;
        const maxX = street.xMax - 50;
        x = minX + Math.random() * (maxX - minX);
        y = street.y;
        orientation = 'H';
        vx = (Math.random() > 0.5 ? 1 : -1);
        vy = 0;
      } else {
        const minY = street.yMin + 50;
        const maxY = street.yMax - 50;
        x = street.x;
        y = minY + Math.random() * (maxY - minY);
        orientation = 'V';
        vx = 0;
        vy = (Math.random() > 0.5 ? 1 : -1);
      }

      let personalWhiteZones = [];
      let charisma = 1;
      let title = 'Passant';

      if (hasApp) {
        const roll = Math.random();
        if (roll < 0.25) {
          personalWhiteZones = [];
        } else if (roll < 0.65) {
          const p1 = PLACES[Math.floor(Math.random() * PLACES.length)];
          personalWhiteZones = [p1.id];
        } else {
          const shuffledPlaces = [...PLACES].sort(() => Math.random() - 0.5);
          personalWhiteZones = [shuffledPlaces[0].id, shuffledPlaces[1].id];
        }

        // Attribution du Charisme RPG (1 à 3 étoiles)
        const charRoll = Math.random();
        if (charRoll < 0.3) {
          charisma = 3;
          title = 'Protecteur Vétéran 🛡️';
        } else if (charRoll < 0.7) {
          charisma = 2;
          title = 'Protecteur Athlétique 🏃';
        } else {
          charisma = 1;
          title = 'Protecteur Citoyen 👤';
        }
      }

      newCitizens.push({
        id: i,
        x,
        y,
        orientation,
        vx,
        vy,
        hasApp,
        role: hasApp ? 'guardian' : 'pedestrian',
        charisma,
        guardianTitle: title,
        personalWhiteZones,
        hasReceivedAlert: false,
        hasDeclined: false,
        declinedReason: null,
        isResponding: false,
        hasTouchedVictim: false,
        hasDamagedAttacker: false,
        isInPersonalWhiteZone: false,
        pauseTimer: 0,
      });
    }

    setSimulationState(prev => ({
      ...prev,
      citizens: newCitizens,
      activeAlert: false,
      attacker: null,
      policeCar: null,
      meshConnections: [],
      duressPinTriggered: false,
    }));
  }, [simulationConfig.citizenCount, simulationConfig.appAdoptionRate]);

  useEffect(() => {
    initCitizens();
  }, [initCitizens]);

  const handleStopCrisis = () => {
    // Quitte complètement l'évènement en cours et revient à l'écran "Démarrer"
    // (avant, seule la crise était effacée : le bouton retombait sur "En cours" au lieu de "Démarrer").
    setIsGameStarted(false);
    setIsPaused(false);
    setIsDuelModalOpen(false);
    setActiveProtector(null);
    hasTriggeredContactDuelRef.current = false;
    incidentFinalizedRef.current = false;
    lastAggressionTickRef.current = null;
    autoCrisisCountdownRef.current = 0;
    setSimulationState(prev => ({
      ...prev,
      activeAlert: false,
      alertStartTime: null,
      alertSimulatedSeconds: 0,
      attacker: null,
      policeCar: null,
      duressPinTriggered: false,
      victim: { ...prev.victim, isConfronting: false, confrontationTimer: 0 },
      citizens: prev.citizens.map(c => ({
        ...c,
        isResponding: false,
        hasReceivedAlert: false,
        hasDeclined: false,
        declinedReason: null,
        hasTouchedVictim: false,
        hasDamagedAttacker: false,
        pauseTimer: 0,
      })),
    }));
    addLog(`Alerte arrêtée. Retour à l'écran de démarrage.`, 'info', '#cbd5e1');
  };

  const handleReset = () => {
    setIncidentReport(null);
    setIsPaused(false);
    setIsGameStarted(false);
    setIsDuelModalOpen(false);
    setActiveProtector(null);
    hasTriggeredContactDuelRef.current = false;
    incidentFinalizedRef.current = false;
    lastAggressionTickRef.current = null;
    autoCrisisCountdownRef.current = 0;
    initCitizens();
    setSimulationState(prev => ({
      ...prev,
      activeAlert: false,
      alertStartTime: null,
      alertSimulatedSeconds: 0,
      attacker: null,
      policeCar: null,
      duressPinTriggered: false,
      victim: {
        x: 260,
        y: 170,
        orientation: 'H',
        vx: 1.0,
        vy: 0,
        profileName: 'Sarah (Vous)',
        isInPersonalWhiteZone: false,
        isConfronting: false,
        confrontationTimer: 0,
        maxConfrontationDuration: 700,
        requiredGuardiansToFlee: 2,
        pauseTimer: 0,
      },
    }));
    setStats({ responseTime: '0s', guardiansAlerted: 0, vaultFiles: 0, meshHops: 0 });
    addLog('Simulation réinitialisée.', 'info', '#cbd5e1');
  };

  const handleCloseReport = () => {
    setIncidentReport(null);
    setIsPaused(false);
    setIsGameStarted(false);
    setIsDuelModalOpen(false);
    setActiveProtector(null);
    setActionHistory([]);
    hasTriggeredContactDuelRef.current = false;
    incidentFinalizedRef.current = false;
    lastAggressionTickRef.current = null;
    autoCrisisCountdownRef.current = 0;

    // Recentrer automatiquement l'écran sur la carte 3D
    setTimeout(() => {
      const mapElement = document.querySelector('.city-3d-canvas-box') || document.querySelector('.simulator-map-card');
      if (mapElement) {
        mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);

    // Réinitialisation propre de la simulation et préservation des citoyens
    initCitizens();
    setSimulationState(prev => ({
      ...prev,
      isNight: false,
      victim: {
        x: 260,
        y: 170,
        orientation: 'H',
        vx: 1.0,
        vy: 0,
        profileName: 'Sarah (Vous)',
        isInPersonalWhiteZone: false,
        isConfronting: false,
        confrontationTimer: 0,
        maxConfrontationDuration: 700,
        requiredGuardiansToFlee: 2,
        pauseTimer: 0,
      },
      childEntity: null,
      attacker: null,
      policeCar: null,
      activeAlert: false,
      alertStartTime: null,
      alertSimulatedSeconds: 0,
      duressPinTriggered: false,
      meshConnections: [],
    }));

    setStats({ responseTime: '0s', guardiansAlerted: 0, vaultFiles: 0, meshHops: 0 });
    
    setLogs([
      { time: new Date().toLocaleTimeString(), text: '✨ Session précédente classée. Simulateur MonAlerte réinitialisé à neuf.', color: '#4ade80', type: 'success' },
      { time: new Date().toLocaleTimeString(), text: 'Prêt pour une nouvelle partie : Configurez vos attributs et démarrez !', color: '#ffd873', type: 'info' },
    ]);

    setIsSetupModalOpen(false);

    // Affiche la popup de félicitations "Maîtrise complète" seulement à la fermeture du rapport
    if (pendingMasteryPopupRef.current) {
      pendingMasteryPopupRef.current = false;
      setShowMasteryPopup(true);
    }
  };

  // Sortie du téléphone en main : GRATUIT (0 PV, 0 Agilité, 0 Charisme) dès qu'un Protecteur arrive au contact
  const handleDrawPhone = (newLocation) => {
    setCurrentPhoneLocation(newLocation);
    addLog(`📱 Téléphone sorti dans la main (Gratuit grâce à la diversion du Protecteur) !`, 'success', '#38bdf8');
  };

  // Gestion des actions de Sarah dans la modale tour par tour (Instantané et Gratuit)
  const handleVictimAction = ({ actionId, success }) => {
    sfx.playAction();

    setActionHistory(prev => [
      ...prev,
      { actionId, success, timestamp: new Date().toLocaleTimeString() }
    ]);

    if (success) {
      sfx.playAlert();
      wasAlertTriggeredRef.current = true;
      const contactsCount = trustedContactsCountRef.current;
      if (contactsCount > 0) {
        const contactsSeen = Math.min(contactsCount, Math.max(1, Math.floor(contactsCount * 0.7) + (Math.random() > 0.5 ? 1 : 0)));
        addLog(`📲 CONTACTS : ${contactsCount} SMS d'urgence envoyés avec lien de secours géolocalisé, ${contactsSeen} ont ouvert l'alerte !`, 'info', '#f2b807');
      } else {
        addLog(`⚠️ CONTACTS : 0 Contact de Confiance configuré. Aucun SMS d'urgence envoyé !`, 'alert', '#f59e0b');
      }

      const isGuardiansActive = Boolean(unlockedFeatures.includes('guardians'));

      if (isGuardiansActive) {
        addLog(`⚡ RÉUSSITE TOUR PAR TOUR : Alerte transmise à la Communauté MonAlerte !`, 'success', '#10b981');
      } else {
        addLog(`⚠️ INFORMATION : Aucun Protecteur alerté (Tuile 'Alerter la Communauté' non débloquée).`, 'alert', '#f59e0b');
      }

      setSimulationState(prev => {
        const targetEntity = (selectedRole === 'parent' && prev.childEntity) ? prev.childEntity : prev.victim;
        const eligibleAtT0 = prev.citizens.filter(c => {
          const inWZ = c.hasApp && c.personalWhiteZones && c.personalWhiteZones.some(placeId => {
            const p = PLACES.find(pl => pl.id === placeId);
            return p && Math.hypot(c.x - p.x, c.y - p.y) < p.radius;
          });
          return c.hasApp && !inWZ && Math.hypot(c.x - targetEntity.x, c.y - targetEntity.y) <= 270;
        }).map(c => c.id);

        const shuffled = [...eligibleAtT0].sort(() => Math.random() - 0.5);
        // Taux d'acceptation proportionnel dynamique : ~75% des Protecteurs ayant reçu l'alerte acceptent et interviennent
        const acceptedCount = eligibleAtT0.length > 0 ? Math.max(1, Math.round(eligibleAtT0.length * 0.75)) : 0;
        const respondersAtT0 = isGuardiansActive ? shuffled.slice(0, acceptedCount) : [];

        if (isGuardiansActive) {
          addLog(`🚨 SOS RÉSEAU TRANSMIS à ${eligibleAtT0.length} Protecteur(s) dans le secteur (${respondersAtT0.length} ont accepté et convergent immédiatement) !`, 'alert', '#ef4444');
        }

        const updatedCitizens = prev.citizens.map(c => {
          if (eligibleAtT0.includes(c.id)) {
            const isResp = respondersAtT0.includes(c.id);
            return {
              ...c,
              hasReceivedAlert: isGuardiansActive,
              isResponding: isResp,
              hasDeclined: isGuardiansActive && !isResp,
              declinedReason: isResp ? null : 'busy_or_declined',
              pauseTimer: 0,
            };
          }
          return c;
        });

        return {
          ...prev,
          activeAlert: true,
          alertStartTime: Date.now(),
          citizens: updatedCitizens,
        };
      });
    } else {
      addLog(`❌ ÉCHEC DU SOS APP : L'agresseur a contré l'appui sur l'écran tactile ! Utilisez vos raccourcis matériels discrets ou l'aide d'un Protecteur.`, 'alert', '#ef4444');
    }
  };

  const handleFinishIncident = useCallback(() => {
    setIsDuelModalOpen(false);
    setIsPaused(true);

    // Garde anti-double-comptage : évite de reverser les récompenses si l'incident a déjà été clôturé
    // (ex. par la résolution automatique de fuite dans la boucle de simulation).
    if (incidentFinalizedRef.current) return;
    incidentFinalizedRef.current = true;

    const isCameraActive = (unlockedFeaturesRef.current || []).includes('cameraCloud');
    // Identifié aussi si le téléphone était en main et que l'agresseur l'a arraché : la vidéo/audio a déjà capté la scène avant le vol
    const phoneSnatchedFromHand = phoneLocationAtAlert === 'in_hand' && currentPhoneLocation === 'confiscated';
    const isPhoneInHand = (currentPhoneLocation === 'in_hand') || phoneSnatchedFromHand;
    const alertTriggered = Boolean(simulationState.activeAlert || wasAlertTriggeredRef.current);
    const isSuspectIdentified = alertTriggered && isCameraActive && isPhoneInHand;
    const videoIdentified = isSuspectIdentified;
    const videoObscuredReason = !alertTriggered 
      ? 'no_alert_triggered'
      : (!isCameraActive ? 'camera_not_unlocked' : (!isPhoneInHand ? currentPhoneLocation : null));
    const identificationMethod = isSuspectIdentified ? 'video' : 'none';

    const fleePct = simulationState.attacker?.fleeProgressPct !== undefined 
      ? simulationState.attacker.fleeProgressPct 
      : (simulationState.victim?.confrontationTimer && simulationState.victim?.maxConfrontationDuration 
          ? Math.min(90, Math.max(10, Math.round((simulationState.victim.confrontationTimer / simulationState.victim.maxConfrontationDuration) * 100))) 
          : 35);

    checkMasteryAchievement(fleePct < 100, isSuspectIdentified);

    const allCits = simulationState.citizens || [];
    const totalArrived = allCits.filter(c => c.hasApp && c.isResponding && (c.hasTouchedVictim || Math.hypot(c.x - simulationState.victim.x, c.y - simulationState.victim.y) <= 20)).length;
    const totalReceived = allCits.filter(c => c.hasApp && c.hasReceivedAlert).length;
    const totalAccepted = allCits.filter(c => c.hasApp && c.isResponding).length;
    const whiteZoneInAlert = allCits.filter(c => c.hasApp && c.isInPersonalWhiteZone && Math.hypot(c.x - simulationState.victim.x, c.y - simulationState.victim.y) <= 270).length;

    const rewards = calculateRewards({
      fleeProgressPct: fleePct,
      isIdentified: isSuspectIdentified,
      alertTriggered,
      arrivedCount: Math.max(1, totalArrived),
      contactsCount: trustedContactsCountRef.current,
    });

    setUserXP(prevXP => {
      const nextXP = prevXP + rewards.totalXP;
      saveUserXP(nextXP);
      return nextXP;
    });

    setUserPV(prevPV => {
      const nextPV = prevPV + rewards.totalPV;
      saveUserPV(nextPV);
      return nextPV;
    });

    finalizeIncidentReport({
      duration: `${Math.max(10, Math.round(simulationState.alertSimulatedSeconds || 12))}s`,
      alertTriggered,
      xpReward: rewards,
      userXP: (userXPRef.current || 0) + rewards.totalXP,
      userPV: (userPVRef.current || 0) + rewards.totalPV,
      receivedCount: totalReceived,
      acceptedCount: totalAccepted,
      arrivedCount: Math.max(1, totalArrived),
      whiteZoneDeclinedCount: whiteZoneInAlert,
      vaultFiles: alertTriggered ? (isCameraActive ? 3 : 1) : 0,
      policeStatus: 'Transmis au Parquet & Commissariat',
      resolution: `L'agresseur a pris la fuite face à l'intervention des Protecteurs (agression interrompue à ${fleePct}% de son forfait).`,
      isIdentified: isSuspectIdentified,
      identificationMethod,
      videoIdentified,
      videoObscuredReason,
      phoneLocationAtAlert,
      phoneLocationFinal: currentPhoneLocation,
      fleeProgressPct: fleePct,
      fleeReason: 'charisma_intimidation',
    }, fleePct < 100, isSuspectIdentified);

    addLog(`🏆 KO DISSUASION : L'agresseur a pris la fuite face aux Protecteurs ! Rapport d'enquête ouvert directement.`, 'success', '#10b981');
  }, [simulationState, currentPhoneLocation, phoneLocationAtAlert, addLog, checkMasteryAchievement, finalizeIncidentReport]);

  const handleContinueSimulation = () => {
    if (simulationState.attacker?.charismaHp <= 0 || simulationState.attacker?.isFleeing) {
      handleFinishIncident();
      return;
    }
    setIsDuelModalOpen(false);
    setIsPaused(false);
  };

  const handleDoNothing = () => {
    setIsDuelModalOpen(false);
    setIsPaused(false);
    addLog(`⚠️ Vous avez choisi de ne rien faire : Aucune alerte déclenchée, l'agression se poursuit sans riposte.`, 'alert', '#f59e0b');
  };

  // Moteur de Déplacement & Pathfinding RPG
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setSimulationState(prev => {
        const speed = timeMultiplier * 0.16;
        const victim = { ...prev.victim };
        const targetEntity = (selectedRole === 'parent' && prev.childEntity) ? prev.childEntity : victim;
        let attacker = prev.attacker ? { ...prev.attacker } : null;
        let activeAlert = prev.activeAlert;
        let alertStartTime = prev.alertStartTime;
        let updatedCitizensList = [...prev.citizens];

        // 1. DÉCLENCHEMENT AUTOMATIQUE APRÈS 5 SECONDES RÉELLES :
        if (isGameStarted && autoCrisisCountdownRef.current > 0 && !attacker && !activeAlert) {
          autoCrisisCountdownRef.current -= 50; // 50ms réelles par tick de simulation

          if (autoCrisisCountdownRef.current <= 0) {
            autoCrisisCountdownRef.current = 0;

            // Tirage aléatoire d'un agresseur (25% contre App SOS, 75% sans contre)
            const pickedArchetype = getRandomAttacker();
            setCurrentAttackerRPG(pickedArchetype);
            const maxHp = pickedArchetype.maxHp || 65;

            // Spawn prévisible à 65px sur le même axe de rue pour garantir la collision
            let spawnX = targetEntity.x;
            let spawnY = targetEntity.y;
            let spawnOrient = targetEntity.orientation;

            if (spawnOrient === 'H') {
              spawnX = (targetEntity.vx > 0 ? targetEntity.x + 65 : targetEntity.x - 65);
              if (spawnX < 120) spawnX = 120;
              if (spawnX > 980) spawnX = 980;
            } else {
              spawnY = (targetEntity.vy > 0 ? targetEntity.y + 65 : targetEntity.y - 65);
              if (spawnY < 100) spawnY = 100;
              if (spawnY > 500) spawnY = 500;
            }

            const maxDurationSec = Math.floor(Math.random() * 15) + 20; // 20 à 35 secondes
            const maxConfTicks = maxDurationSec * 20;

            attacker = {
              x: spawnX,
              y: spawnY,
              orientation: spawnOrient,
              active: true,
              isConfronting: false,
              isFleeing: false,
              fleeTimer: 0,
              hasStolenPhone: false,
              alias: pickedArchetype.alias,
              maxHp: maxHp,
              charismaHp: maxHp,
              immunity: pickedArchetype.immunity,
              weakness: pickedArchetype.weakness,
              requiredCharisma: pickedArchetype.requiredCharisma || 2,
              actionText: 'Rapprochement sur le trottoir',
              maxDurationSec: maxDurationSec,
              remainingSec: maxDurationSec,
            };

            victim.maxConfrontationDuration = maxConfTicks;
            victim.requiredGuardiansToFlee = pickedArchetype.requiredCharisma || 2;
            victim.confrontationTimer = 0;
            victim.isConfronting = false;

            addLog(`⚠️ Un individu suspect fond sur vous à vive allure !`, 'alert', '#ef4444');
          }
        }

        // 2. Déplacement Victime (Sarah)
        let vx = victim.vx;
        let vy = victim.vy;
        let x = victim.x;
        let y = victim.y;
        let orient = victim.orientation;
        let isConfronting = victim.isConfronting;
        let confTimer = victim.confrontationTimer || 0;
        let maxConf = victim.maxConfrontationDuration || 700;
        let vPause = victim.pauseTimer || 0;

        if (vPause > 0) {
          vPause--;
        } else if (!isConfronting) {
          if (orient === 'H') {
            x += vx * speed;
            if (x <= 100) { x = 100; vx = 1; }
            else if (x >= 1010) { x = 1010; vx = -1; }

            INTERSECTIONS.forEach(inter => {
              if (Math.abs(x - inter.x) <= 3 && Math.abs(y - inter.y) <= 3 && Math.random() < 0.1) {
                orient = 'V';
                x = inter.x;
                vy = (Math.random() > 0.5 ? 1 : -1);
                vx = 0;
              }
            });
          } else {
            y += vy * speed;
            if (y <= 80) { y = 80; vy = 1; }
            else if (y >= 530) { y = 530; vy = -1; }

            INTERSECTIONS.forEach(inter => {
              if (Math.abs(x - inter.x) <= 3 && Math.abs(y - inter.y) <= 3 && Math.random() < 0.1) {
                orient = 'H';
                y = inter.y;
                vx = (Math.random() > 0.5 ? 1 : -1);
                vy = 0;
              }
            });
          }

          PLACES.forEach(pl => {
            if (Math.hypot(x - pl.x, y - pl.y) < 18 && Math.random() < 0.006) {
              vPause = 140;
            }
          });
        }

        // 3. Déplacement Enfant (si mode parent)
        let child = prev.childEntity;
        if (child) {
          let cx = child.x;
          let cy = child.y;
          let cvx = child.vx;
          let cvy = child.vy;
          let cOrient = child.orientation;
          let cPause = child.pauseTimer || 0;

          if (isConfronting && selectedRole === 'parent') {
            // Immobilisé
          } else if (cPause > 0) {
            cPause--;
          } else {
            if (cOrient === 'H') {
              cx += cvx * speed * 0.85;
              if (cx <= 100) { cx = 100; cvx = 1; }
              else if (cx >= 1010) { cx = 1010; cvx = -1; }

              INTERSECTIONS.forEach(inter => {
                if (Math.abs(cx - inter.x) <= 3 && Math.abs(cy - inter.y) <= 3 && Math.random() < 0.1) {
                  cOrient = 'V';
                  cx = inter.x;
                  cvy = (Math.random() > 0.5 ? 1 : -1);
                  cvx = 0;
                }
              });
            } else {
              cy += cvy * speed * 0.85;
              if (cy <= 80) { cy = 80; cvy = 1; }
              else if (cy >= 530) { cy = 530; cvy = -1; }

              INTERSECTIONS.forEach(inter => {
                if (Math.abs(cx - inter.x) <= 3 && Math.abs(cy - inter.y) <= 3 && Math.random() < 0.1) {
                  cOrient = 'H';
                  cy = inter.y;
                  cvx = (Math.random() > 0.5 ? 1 : -1);
                  cvy = 0;
                }
              });
            }
          }

          child = { ...child, x: cx, y: cy, vx: cvx, vy: cvy, orientation: cOrient, pauseTimer: cPause };
        }

        // 4. Gestion de l'Agresseur & Lutte au Corps à Corps
        if (attacker && attacker.active) {
          if (attacker.isFleeing) {
            attacker.fleeTimer = (attacker.fleeTimer || 0) + 1;
            if (attacker.orientation === 'H') {
              attacker.x += (attacker.fleeDirX || 1) * speed * 2.8;
            } else {
              attacker.y += (attacker.fleeDirY || 1) * speed * 2.8;
            }

            const fleeDist = Math.hypot(attacker.x - targetEntity.x, attacker.y - targetEntity.y);

            if (fleeDist > 70 || attacker.fleeTimer > 35 || attacker.x >= 1040 || attacker.x <= 40 || attacker.y >= 560 || attacker.y <= 40) {
              activeAlert = false;
              attacker.active = false;
              attacker.isFleeing = false;
              isConfronting = false;

              // Garde anti-double-comptage : le StrictMode de React peut ré-invoquer cet updater
              // (et le rapport peut aussi être clôturé manuellement) ; on ne verse les récompenses qu'une fois.
              const shouldFinalizeIncident = !incidentFinalizedRef.current;
              if (shouldFinalizeIncident) {
                incidentFinalizedRef.current = true;
                setIsPaused(true);
              }

              const totalDurationSec = Math.max(10, Math.round(prev.alertSimulatedSeconds || 12));
              
              const totalReceived = updatedCitizensList.filter(c => c.hasApp && c.hasReceivedAlert).length;
              const totalAccepted = updatedCitizensList.filter(c => c.hasApp && c.isResponding).length;
              const totalArrived = updatedCitizensList.filter(c => c.hasApp && c.isResponding && (c.hasTouchedVictim || Math.hypot(c.x - targetEntity.x, c.y - targetEntity.y) <= 14)).length;
              
              const whiteZoneInAlert = updatedCitizensList.filter(c => c.hasApp && c.isInPersonalWhiteZone && Math.hypot(c.x - targetEntity.x, c.y - targetEntity.y) <= 270).length;

              const alertTriggered = Boolean(prev.activeAlert || wasAlertTriggeredRef.current);

              // Identification du suspect : par la Capture Vidéo avec le smartphone EN MAIN (ou arraché depuis la main) !
              const isCameraActive = (unlockedFeaturesRef.current || []).includes('cameraCloud');
              const phoneSnatchedFromHand = phoneLocationAtAlert === 'in_hand' && currentPhoneLocation === 'confiscated';
              const isPhoneInHand = (currentPhoneLocation === 'in_hand') || phoneSnatchedFromHand;
              const isSuspectIdentified = alertTriggered && isCameraActive && isPhoneInHand;
              const videoIdentified = isSuspectIdentified;
              const videoObscuredReason = !alertTriggered 
                ? 'no_alert_triggered'
                : (!isCameraActive ? 'camera_not_unlocked' : (!isPhoneInHand ? currentPhoneLocation : null));
              const identificationMethod = isSuspectIdentified ? 'video' : 'none';

              const fleePct = attacker.fleeProgressPct !== undefined 
                ? attacker.fleeProgressPct 
                : (attacker.fleeReason === 'charisma_intimidation' ? 65 : 100);

              const resolutionText = fleePct < 100
                ? `L'agresseur a pris la fuite à ${fleePct}% d'avoir accompli son agression (mis en déroute par l'intervention des Protecteurs) !`
                : `Malheureusement, l'agresseur a eu gain de cause et a réussi son agression (100% du forfait accompli).`;

              // Calcul des gains d'expérience (XP) et de Points de Vie (PV)
              const rewards = calculateRewards({
                fleeProgressPct: fleePct,
                isIdentified: isSuspectIdentified,
                alertTriggered,
                arrivedCount: totalArrived,
                contactsCount: trustedContactsCountRef.current,
              });

              if (shouldFinalizeIncident) {
              checkMasteryAchievement(fleePct < 100, isSuspectIdentified);

              setUserXP(prevXP => {
                const nextXP = prevXP + rewards.totalXP;
                saveUserXP(nextXP);
                return nextXP;
              });

              setUserPV(prevPV => {
                const nextPV = prevPV + rewards.totalPV;
                saveUserPV(nextPV);
                return nextPV;
              });

              finalizeIncidentReport({
                duration: `${totalDurationSec}s`,
                alertTriggered,
                xpReward: rewards,
                userXP: (userXPRef.current || 0) + rewards.totalXP,
                userPV: (userPVRef.current || 0) + rewards.totalPV,
                receivedCount: totalReceived,
                acceptedCount: totalAccepted,
                arrivedCount: totalArrived,
                whiteZoneDeclinedCount: whiteZoneInAlert,
                vaultFiles: alertTriggered ? (isCameraActive ? 3 : 1) : 0,
                policeStatus: 'Transmis au Parquet & Commissariat',
                resolution: resolutionText,
                isIdentified: isSuspectIdentified,
                identificationMethod,
                videoIdentified,
                videoObscuredReason,
                phoneLocationAtAlert,
                phoneLocationFinal: currentPhoneLocation,
                fleeProgressPct: fleePct,
                fleeReason: attacker.fleeReason || (fleePct < 100 ? 'charisma_intimidation' : 'attack_completed_or_snatched'),
              }, fleePct < 100, isSuspectIdentified);

              addLog(`💖 RÉCOMPENSES GAGNÉES : +${rewards.totalPV} PV & +${rewards.totalXP} XP obtenus lors de cette simulation !`, 'success', '#ffd873');

              if (fleePct < 100) {
                addLog(`🏁 FIN DE L’INCIDENT : L'agresseur a pris la fuite à ${fleePct}% grâce aux Protecteurs. Rapport d'enquête généré !`, 'success', '#4ade80');
              } else {
                addLog(`🏁 FIN DE L’INCIDENT : Malheureusement, l'agresseur a eu gain de cause et a réussi son agression (100%). Rapport d'enquête généré !`, 'alert', '#ef4444');
              }
              }
            }
          } else if (attacker.isConfronting) {
            attacker.x = targetEntity.x + 2;
            attacker.y = targetEntity.y;
            isConfronting = true;
            confTimer += timeMultiplier;

            const remaining = Math.max(0, Math.ceil((maxConf - confTimer) / 20));
            attacker.remainingSec = remaining;

            // Petit "tac" sonore à chaque seconde écoulée du décompte de l'agression
            if (lastAggressionTickRef.current === null) {
              lastAggressionTickRef.current = remaining;
            } else if (remaining < lastAggressionTickRef.current) {
              sfx.playTick();
              lastAggressionTickRef.current = remaining;
            }

            // DÉCLENCHEMENT DU TOUR PAR TOUR (PAUSE AUTOMATIQUE LORS DE L'AGRESSION)
            if (!hasTriggeredContactDuelRef.current && selectedRole === 'user') {
              hasTriggeredContactDuelRef.current = true;

              // Emplacement aléatoire du téléphone : 33% Sac, 33% Poche, 33% Main
              const randomPhoneLoc = getRandomPhoneLocation();
              setPhoneLocationAtAlert(randomPhoneLoc);

              // Si le mobile est en main, 25% de chances que l'agresseur l'arrache immédiatement au contact
              // (forcé si les 2 dernières fois en main, il n'a pas été arraché)
              // Ce risque n'existe que si la Touche Raccourcis est débloquée (sinon rien à contrer en main)
              const hasShortcutHardware = (unlockedFeaturesRef.current || []).includes('shortcutHardware');
              const snatchRoll = shouldSnatchPhone(randomPhoneLoc);
              const isSnatchedFromHand = hasShortcutHardware && snatchRoll;
              const finalPhoneLoc = isSnatchedFromHand ? 'confiscated' : randomPhoneLoc;
              setCurrentPhoneLocation(finalPhoneLoc);

              const locLabels = {
                'in_bag': '👜 dans le Sac à main (33%)',
                'in_pocket': '👖 dans la Poche (33%)',
                'in_hand': '📱 déjà en Main (33%)',
              };
              addLog(`🎲 EMPLACEMENT DU TÉLÉPHONE : Au moment de l'alerte, le mobile est ${locLabels[randomPhoneLoc] || randomPhoneLoc} !`, 'info', '#38bdf8');

              if (isSnatchedFromHand) {
                addLog(`✋ L'agresseur a arraché le téléphone des mains dès le contact (25% de risque) !`, 'alert', '#ef4444');
              }

              setIsPaused(true);
              setDuelPhase('victim_action');
              setIsDuelModalOpen(true);
              addLog(`🚨 AGRESSION EN COURS : Le jeu se met en pause ! Choisissez votre réaction de défense.`, 'alert', '#ef4444');
            }

            // Si le compte à rebours d'agression expire (avant mise en fuite par les protecteurs)
            if (confTimer >= maxConf && !attacker.isFleeing) {
              attacker.isFleeing = true;
              attacker.fleeReason = 'attack_completed_or_snatched';
              attacker.fleeProgressPct = 100;
              attacker.isConfronting = false;
              attacker.fleeTimer = 0;
              attacker.fleeDirX = attacker.x > 540 ? 1 : -1;
              attacker.fleeDirY = 0;
              isConfronting = false;
              sfx.playAttackerFlee();
              addLog(`💨 Compte à rebours écoulé (100%) : L'agresseur a accompli son agression avant de fuir !`, 'alert', '#f59e0b');
            }
          } else {
            // Approche de l'agresseur : VITESSE MAXIMALE (speed * 2.2)
            const targetX = targetEntity.x;
            const targetY = targetEntity.y;
            const attackerSpeed = speed * 2.2;

            const distToTarget = Math.hypot(attacker.x - targetX, attacker.y - targetY);
            if (distToTarget <= 18) {
              attacker.isConfronting = true;
              attacker.x = targetX + 2;
              attacker.y = targetY;
              attacker.actionText = 'Lutte au corps à corps !';
              isConfronting = true;
              confTimer = 0;
              sfx.playAttackerGrab();
              lastAggressionTickRef.current = null;
              addLog(`💥 "${attacker.alias || 'L’agresseur'}" a agrippé ${selectedRole === 'parent' && prev.childEntity ? prev.childEntity.name : 'Sarah'} !`, 'alert', '#ef4444');
            } else if (attacker.orientation === 'H') {
              if (Math.abs(attacker.y - targetY) < 6) {
                const adx = targetX - attacker.x;
                if (Math.abs(adx) > 6) {
                  attacker.x += (adx > 0 ? 1 : -1) * attackerSpeed;
                } else {
                  attacker.isConfronting = true;
                  attacker.x = targetX + 2;
                  attacker.y = targetY;
                  attacker.actionText = 'Lutte au corps à corps !';
                  isConfronting = true;
                  confTimer = 0;
                  sfx.playAttackerGrab();
                  lastAggressionTickRef.current = null;
                  addLog(`💥 "${attacker.alias || 'L’agresseur'}" a agrippé ${selectedRole === 'parent' && prev.childEntity ? prev.childEntity.name : 'Sarah'} !`, 'alert', '#ef4444');
                }
              } else {
                let bestInterX = VERT_STREETS[0];
                let minD = 99999;
                VERT_STREETS.forEach(ix => {
                  const d = Math.abs(ix - attacker.x) + Math.abs(targetY - attacker.y) + Math.abs(targetX - ix);
                  if (d < minD) { minD = d; bestInterX = ix; }
                });

                const adx = bestInterX - attacker.x;
                if (Math.abs(adx) > attackerSpeed) {
                  attacker.x += (adx > 0 ? 1 : -1) * attackerSpeed;
                } else {
                  attacker.x = bestInterX;
                  attacker.orientation = 'V';
                }
              }
            } else {
              if (Math.abs(attacker.x - targetX) < 6) {
                const ady = targetY - attacker.y;
                if (Math.abs(ady) > 6) {
                  attacker.y += (ady > 0 ? 1 : -1) * attackerSpeed;
                } else {
                  attacker.isConfronting = true;
                  attacker.x = targetX;
                  attacker.y = targetY + 2;
                  attacker.actionText = 'Lutte au corps à corps !';
                  isConfronting = true;
                  confTimer = 0;
                  sfx.playAttackerGrab();
                  lastAggressionTickRef.current = null;
                  addLog(`💥 "${attacker.alias || 'L’agresseur'}" a agrippé ${selectedRole === 'parent' && prev.childEntity ? prev.childEntity.name : 'Sarah'} !`, 'alert', '#ef4444');
                }
              } else {
                let bestInterY = HORIZ_STREETS[0];
                let minD = 99999;
                HORIZ_STREETS.forEach(iy => {
                  const d = Math.abs(iy - attacker.y) + Math.abs(targetX - attacker.x) + Math.abs(targetY - iy);
                  if (d < minD) { minD = d; bestInterY = iy; }
                });

                const ady = bestInterY - attacker.y;
                if (Math.abs(ady) > attackerSpeed) {
                  attacker.y += (ady > 0 ? 1 : -1) * attackerSpeed;
                } else {
                  attacker.y = bestInterY;
                  attacker.orientation = 'H';
                }
              }
            }
          }
        }

        // 5. Gestion des Citoyens & Déplacement des Protecteurs
        let protectorStrikeTriggeredInThisTick = false;
        const updatedCitizens = updatedCitizensList.map(c => {
          let cx = c.x;
          let cy = c.y;
          let cvx = c.vx;
          let cvy = c.vy;
          let cOrient = c.orientation;
          let isResp = c.isResponding;
          let hasRec = c.hasReceivedAlert;
          let hasDec = c.hasDeclined;
          let decReason = c.declinedReason;
          let hasTouched = c.hasTouchedVictim || false;
          let hasDamaged = c.hasDamagedAttacker || false;
          let cPause = c.pauseTimer || 0;

          const inWhiteZone = c.hasApp && c.personalWhiteZones && c.personalWhiteZones.some(placeId => {
            const p = PLACES.find(pl => pl.id === placeId);
            return p && Math.hypot(cx - p.x, cy - p.y) < p.radius;
          });

          if (!activeAlert) {
            isResp = false;
            hasRec = false;
            hasDec = false;
            decReason = null;
            hasTouched = false;
          }

          if (isResp && !attacker?.isFleeing) {
            cPause = 0;
            const targetX = targetEntity.x;
            const targetY = targetEntity.y;

            if (cOrient === 'H') {
              if (Math.abs(cy - targetY) < 4) {
                cvx = (targetX >= cx ? 1 : -1);
                const dx = targetX - cx;
                if (Math.abs(dx) > 4) {
                  cx += cvx * speed * 1.5;
                } else {
                  cx = targetX;
                  hasTouched = true;
                }
              } else {
                let bestInterX = VERT_STREETS[0];
                let minD = 99999;
                VERT_STREETS.forEach(ix => {
                  const d = Math.abs(ix - cx) + Math.abs(targetY - cy) + Math.abs(targetX - ix);
                  if (d < minD) { minD = d; bestInterX = ix; }
                });

                cvx = (bestInterX >= cx ? 1 : -1);
                const dx = bestInterX - cx;

                if (Math.abs(dx) > speed * 1.5) {
                  cx += cvx * speed * 1.5;
                } else {
                  cx = bestInterX;
                  cOrient = 'V';
                  cvy = (targetY >= cy ? 1 : -1);
                }
              }
            } else {
              if (Math.abs(cx - targetX) < 4) {
                cvy = (targetY >= cy ? 1 : -1);
                const dy = targetY - cy;
                if (Math.abs(dy) > 4) {
                  cy += cvy * speed * 1.5;
                } else {
                  cy = targetY;
                  hasTouched = true;
                }
              } else {
                let bestInterY = HORIZ_STREETS[0];
                let minD = 99999;
                HORIZ_STREETS.forEach(iy => {
                  const d = Math.abs(iy - cy) + Math.abs(targetX - cx) + Math.abs(targetY - iy);
                  if (d < minD) { minD = d; bestInterY = iy; }
                });

                cvy = (bestInterY >= cy ? 1 : -1);
                const dy = bestInterY - cy;

                if (Math.abs(dy) > speed * 1.5) {
                  cy += cvy * speed * 1.5;
                } else {
                  cy = bestInterY;
                  cOrient = 'H';
                  cvx = (targetX >= cx ? 1 : -1);
                }
              }
            }

            // FRAPPE TOUR PAR TOUR DU PROTECTEUR AU CONTACT (1 SEUL PROTECTEUR PAR TOUR/TICK)
            if (Math.hypot(targetX - cx, targetY - cy) <= 14 || hasTouched) {
              hasTouched = true;
              if (!hasDamaged && attacker && !attacker.isFleeing && !protectorStrikeTriggeredInThisTick) {
                hasDamaged = true;
                protectorStrikeTriggeredInThisTick = true;
                const dmg = c.charisma === 3 ? 35 : c.charisma === 2 ? 25 : 15;
                const currentHp = attacker.charismaHp !== undefined ? attacker.charismaHp : (attacker.maxHp || 65);
                const newHp = Math.max(0, currentHp - dmg);
                attacker.charismaHp = newHp;
                sfx.playProtectorImpact();

                // Pause du jeu & Ouverture modale de frappe du protecteur
                setTimeout(() => {
                  setIsPaused(true);
                  setActiveProtector(c);
                  setDuelPhase('protector_strike');
                  setIsDuelModalOpen(true);
                  addLog(`🛡️ [TOUR DU PROTECTEUR] ${c.guardianTitle || 'Protecteur'} intervient ! -${dmg} Charisme infligé (${currentHp} -> ${newHp}/${attacker.maxHp || 65} PV)`, 'success', '#10b981');
                }, 0);

                if (newHp <= 0) {
                  const fleePct = Math.min(95, Math.max(10, Math.round(((victim.confrontationTimer || 0) / (victim.maxConfrontationDuration || 500)) * 100)));
                  attacker.isFleeing = true;
                  attacker.fleeReason = 'charisma_intimidation';
                  attacker.fleeProgressPct = fleePct;
                  attacker.isConfronting = false;
                  attacker.fleeTimer = 0;
                  if (attacker.orientation === 'H') {
                    attacker.fleeDirX = attacker.x > targetEntity.x ? 1 : -1;
                  } else {
                    attacker.fleeDirY = attacker.y > targetEntity.y ? 1 : -1;
                  }
                  sfx.playAttackerFlee();
                  setTimeout(() => {
                    addLog(`🏆 KO DISSUASION : Le charisme de l’agresseur est tombé à 0 ! Il prend la fuite en panique à ${fleePct}% de son agression.`, 'success', '#4ade80');
                  }, 0);
                }
              }
            }
          } else {
            if (cPause > 0) {
              cPause--;
            } else {
              if (cOrient === 'H') {
                cx += cvx * speed * 0.75;
                if (cx <= 100) {
                  cx = 100;
                  cvx = 1;
                } else if (cx >= 1010) {
                  cx = 1010;
                  cvx = -1;
                }

                INTERSECTIONS.forEach(inter => {
                  if (Math.abs(cx - inter.x) <= 3 && Math.abs(cy - inter.y) <= 3 && Math.random() < 0.08) {
                    cOrient = 'V';
                    cx = inter.x;
                    cy = inter.y;
                    cvy = (Math.random() > 0.5 ? 1 : -1);
                    cvx = 0;
                  }
                });
              } else {
                cy += cvy * speed * 0.75;
                if (cy <= 80) {
                  cy = 80;
                  cvy = 1;
                } else if (cy >= 530) {
                  cy = 530;
                  cvy = -1;
                }

                INTERSECTIONS.forEach(inter => {
                  if (Math.abs(cx - inter.x) <= 3 && Math.abs(cy - inter.y) <= 3 && Math.random() < 0.08) {
                    cOrient = 'H';
                    cx = inter.x;
                    cy = inter.y;
                    cvx = (Math.random() > 0.5 ? 1 : -1);
                    cvy = 0;
                  }
                });
              }

              if (!isResp && cPause <= 0) {
                PLACES.forEach(pl => {
                  if (Math.hypot(cx - pl.x, cy - pl.y) < 14) {
                    if (c.personalWhiteZones && c.personalWhiteZones.includes(pl.id)) {
                      if (Math.random() < 0.015) {
                        cPause = 40 + Math.floor(Math.random() * 30); // Pause temporaire de 2 secondes
                      }
                    } else if (Math.random() < 0.005) {
                      cPause = 20;
                    }
                  }
                });
              }
            }
          }

          return {
            ...c,
            x: cx,
            y: cy,
            vx: cvx,
            vy: cvy,
            orientation: cOrient,
            hasReceivedAlert: hasRec,
            hasDeclined: hasDec,
            declinedReason: decReason,
            isResponding: isResp,
            hasTouchedVictim: hasTouched,
            hasDamagedAttacker: hasDamaged,
            isInPersonalWhiteZone: inWhiteZone,
            pauseTimer: cPause,
          };
        });

        // Chrono basé sur le temps simulé accéléré
        let alertSimulatedSeconds = prev.alertSimulatedSeconds || 0;
        if (activeAlert) {
          alertSimulatedSeconds += 0.05 * timeMultiplier;
          setStats(s => ({
            ...s,
            responseTime: `${Math.round(alertSimulatedSeconds)}s`,
            guardiansAlerted: updatedCitizens.filter(c => c.isResponding).length,
            vaultFiles: parentTiles.cameraCloud ? 3 : 1,
          }));
        }

        return {
          ...prev,
          activeAlert,
          alertStartTime,
          alertSimulatedSeconds,
          victim: {
            ...victim,
            x,
            y,
            vx,
            vy,
            orientation: orient,
            isConfronting,
            confrontationTimer: confTimer,
            pauseTimer: vPause,
          },
          childEntity: child,
          attacker,
          citizens: updatedCitizens,
        };
      });
    }, 50);

    return () => clearInterval(interval);
  }, [
    isPaused, 
    timeMultiplier, 
    addLog, 
    parentTiles.cameraCloud, 
    parentTiles.guardians, 
    isGameStarted, 
    selectedRole, 
    currentAttackerRPG,
    currentPhoneLocation,
    phoneLocationAtAlert,
    checkMasteryAchievement,
    finalizeIncidentReport,
  ]);

  // Calcul sécurisé de la télémétrie réelle de la communauté sur la carte
  const targetEntityForTelemetry = (selectedRole === 'parent' && simulationState?.childEntity) ? simulationState.childEntity : simulationState?.victim;
  const victimX = targetEntityForTelemetry?.x ?? 260;
  const victimY = targetEntityForTelemetry?.y ?? 170;
  
  const allCitizens = simulationState?.citizens || [];
  const inAreaAppCitizens = allCitizens.filter(c => c && c.hasApp && Math.hypot((c.x ?? 0) - victimX, (c.y ?? 0) - victimY) <= 270);
  const inWhiteZoneAppCitizens = inAreaAppCitizens.filter(c => {
    return c.personalWhiteZones && c.personalWhiteZones.some(placeId => {
      const p = PLACES.find(pl => pl.id === placeId);
      return p && Math.hypot((c.x ?? 0) - p.x, (c.y ?? 0) - p.y) < p.radius;
    });
  });
  const eligibleAppCitizens = inAreaAppCitizens.filter(c => !inWhiteZoneAppCitizens.some(wz => wz.id === c.id));
  const activeCommunityTelemetry = {
    guardiansArea: inAreaAppCitizens.length,
    guardiansWhiteZone: inWhiteZoneAppCitizens.length,
    guardiansReceived: eligibleAppCitizens.length,
    guardiansAccepted: eligibleAppCitizens.length > 0 ? Math.max(1, Math.round(eligibleAppCitizens.length * 0.75)) : 0,
  };

  // Recentrage automatique sur la carte principale dès qu'aucune modale n'est ouverte
  useEffect(() => {
    const isAnyModalOpen = Boolean(isDuelModalOpen || incidentReport || isSetupModalOpen || isWelcomeModalOpen);
    if (!isAnyModalOpen) {
      const timer = setTimeout(() => {
        const mapElement = document.querySelector('.city-3d-canvas-box') || document.querySelector('.simulator-map-card');
        if (mapElement) {
          mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isDuelModalOpen, incidentReport, isSetupModalOpen, isWelcomeModalOpen]);

  const arrivedCountSafe = allCitizens.filter(c => c?.isResponding && (c.hasTouchedVictim || Math.hypot((c.x ?? 0) - victimX, (c.y ?? 0) - victimY) <= 20)).length;

  return (
    <div className="simulator-page-container" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--color-background)',
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>
      <style>{`
        @media (max-width: 900px) {
          .simulator-page-container {
            height: auto !important;
            min-height: 100vh !important;
            overflow-y: auto !important;
          }
          .simulator-main-content {
            height: auto !important;
            overflow: visible !important;
            padding: 6px 8px !important;
          }
        }
      `}</style>
      <Navbar />

      <main className="simulator-main-content" style={{ flex: 1, minHeight: 0, padding: '8px 16px 10px 16px', maxWidth: '1440px', margin: '0 auto', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
        {/* BLOC PRINCIPAL UNIQUE : CARTE 3D INTERACTIVE */}
        <City3DMap
          simulationState={simulationState}
          simulationConfig={simulationConfig}
          setSimulationConfig={setSimulationConfig}
          parentTiles={parentTiles}
          parentProfile={parentProfile}
          childrenProfiles={childrenProfiles}
          userRPG={userRPG}
          currentAttackerRPG={currentAttackerRPG}
          selectedRole={selectedRole}
          activePlaceId={activePlaceId}
          onSelectPlace={setActivePlaceId}
          onStopCrisis={handleStopCrisis}
          onReset={handleReset}
          timeMultiplier={timeMultiplier}
          setTimeMultiplier={setTimeMultiplier}
          isGameStarted={isGameStarted}
          userXP={userXP}
          userPV={userPV}
          unlockedFeatures={unlockedFeatures}
          onUnlockFeature={handleUnlockFeature}
          trustedContactsCount={trustedContactsCount}
          onAddTrustedContact={handleAddTrustedContact}
          setUserRPG={setUserRPG}
          currentAgility={currentAgility}
          setCurrentAgility={setCurrentAgility}
          currentCharisma={currentCharisma}
          setCurrentCharisma={setCurrentCharisma}
          onSpendPV={handleSpendPV}
          onBuyPopulation={handleBuyPopulation}
          onBuyProtectors={handleBuyProtectors}
          onOpenSetupModal={() => setIsSetupModalOpen(true)}
          onStartDirectly={() => handleStartGame({ role: selectedRole, spawnAggressor: true })}
          onSpawnAggressor={(archetype) => {
            if (archetype) setCurrentAttackerRPG(archetype);
            handleStartGame({ role: selectedRole, attackerRPG: archetype, spawnAggressor: true });
          }}
          duelProps={{
            isOpen: isDuelModalOpen,
            duelPhase,
            attackerProfile: currentAttackerRPG,
            userProfile: userRPG,
            currentAgility,
            currentCharisma,
            onVictimAction: handleVictimAction,
            onDrawPhone: handleDrawPhone,
            currentPhoneLocation,
            activeProtector,
            arrivedProtectorsCount: arrivedCountSafe,
            attackerHp: simulationState?.attacker?.charismaHp !== undefined ? simulationState.attacker.charismaHp : (currentAttackerRPG?.charismaHp || 65),
            maxAttackerHp: simulationState?.attacker?.maxHp || currentAttackerRPG?.maxHp || 65,
            onContinueSimulation: handleContinueSimulation,
            onFinishIncident: handleFinishIncident,
            onDoNothing: handleDoNothing,
            parentTiles,
            actionHistory,
            communityTelemetry: activeCommunityTelemetry,
            remainingSec: simulationState?.attacker?.remainingSec ?? 25,
            unlockedFeatures,
            trustedContactsCount,
          }}
          reportProps={{
            isOpen: Boolean(incidentReport),
            onClose: handleCloseReport,
            reportData: incidentReport,
            userProfile: userRPG,
            attackerProfile: currentAttackerRPG,
            parentTiles,
            userXP,
            userPV,
          }}
          onOpenWelcomeModal={() => setIsWelcomeModalOpen(true)}
        />
      </main>

      {/* POP-UP MODALE DE BIENVENUE & HISTOIRE DU JEU (15 MIN) */}
      <WelcomeGameModal
        isOpen={isWelcomeModalOpen}
        onClose={() => setIsWelcomeModalOpen(false)}
        onStartGame={() => setIsWelcomeModalOpen(false)}
      />

      {/* POP-UP UNIQUE DE FÉLICITATIONS : MAÎTRISE COMPLÈTE DE MONALERTE */}
      <MasteryCongratsModal
        isOpen={showMasteryPopup}
        onClose={() => setShowMasteryPopup(false)}
      />

      {/* POP-UP UNIQUE DE FÉLICITATIONS : TOUTE PREMIÈRE RÉUSSITE COMPLÈTE (juste avant le rapport) */}
      <FirstSuccessPopup
        isOpen={showFirstSuccessPopup}
        onClose={handleCloseFirstSuccessPopup}
      />

      {/* POP-UP MODALE DE CONFIGURATION DE PARTIE ET CHOIX DE RÔLE RPG */}
      <GameSetupModal
        isOpen={isSetupModalOpen}
        onClose={() => setIsSetupModalOpen(false)}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        parentTiles={parentTiles}
        setParentTiles={setParentTiles}
        childProfile={childrenProfiles[0]}
        userRPG={userRPG}
        setUserRPG={setUserRPG}
        onStartGame={handleStartGame}
        userXP={userXP}
        userPV={userPV}
        unlockedFeatures={unlockedFeatures}
        onUnlockFeature={handleUnlockFeature}
        trustedContactsCount={trustedContactsCount}
        onAddTrustedContact={handleAddTrustedContact}
      />
    </div>
  );
};

export default SimulatorPage;
